// Fetch an image URL and encode it as a self-contained data: URI. The export
// Lambda aborts all network fetches during render, and the browser's cached logo
// copy carries no CORS headers, so a plain URL silently vanishes from the PDF —
// embedding the bytes is what makes the export self-contained (cache: 'no-store'
// bypasses that CORS-less cached copy). Shared by `usePnoReportLogo.ts` and (in
// future) `AicaReportV2Drawer.tsx`'s cover logo — see those call sites.
/**
 * @param timeoutMs a stalled endpoint must never hold the caller open
 * indefinitely — the fetch is aborted once this elapses.
 */
export const fetchImageAsDataUrl = async (url: string, timeoutMs: number): Promise<string> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { cache: 'no-store', mode: 'cors', signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // A misconfigured/broken logo URL that resolves to an HTML error page (or
        // any non-image body) would otherwise become a `data:text/html…` URI — a
        // broken <img> with the page-header space still reserved. Reject before
        // the FileReader runs rather than shipping that.
        const contentType = (res.headers.get('content-type') ?? '').toLowerCase();
        if (!contentType.startsWith('image/')) throw new Error(`not an image (content-type: ${contentType || 'absent'})`);
        const blob = await res.blob();
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
        });
    } finally {
        clearTimeout(timer);
    }
};
